import axios, {AxiosError, isAxiosError} from 'axios';

import {apiUrl} from '../../utils/constants';

const api = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface SessionData {
  sessionId: string;
}

export interface Business {
  name: string;
}

export interface SessionResponse {
  amount: number;
  currency: string;
  requestId: string;
  businessName: string;
  successCallbackUrl: string;
  errorCallbackUrl: string;
  error: string;
}

export const session = async (id: string): Promise<SessionResponse> => {
  try {
    const response = await api.get<SessionResponse>(
      `/v1/checkout/session/${id}`
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<SessionResponse>;
      if (axiosError.response?.data?.error) {
        throw new Error(axiosError.response.data.error);
      }
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

interface FlowData {
  referenceId: string;
  token: string;
  actionUrl: string;
}

export interface PrepareCardCheckoutData {
  cardHolderName: string;
  cardNo: string;
  cardType: string;
  expireMonth: string;
  expireYear: string;
  secureToken: string;
}

export interface PrepareCardCheckoutResponse {
  flowData: FlowData;
  message: string;
  requestId: string;
  status: string;
  transactionId: string;
  error: string;
}

export const prepareCardCheckout = async (
  data: PrepareCardCheckoutData
): Promise<PrepareCardCheckoutResponse> => {
  try {
    const response = await api.post<PrepareCardCheckoutResponse>(
      '/v1/checkout/prepare-card',
      data
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<PrepareCardCheckoutResponse>;
      if (axiosError.response?.data?.error) {
        throw new Error(axiosError.response.data.error);
      }
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

export interface BillTo {
  firstName: string;
  lastName: string;
  email: string;
  address1?: string;
  country?: string;
  locality?: string;
  postalCode?: string;
}

export interface CountryData {
  name: string;
  country: string;
  country_code: string;
  continent_code: string;
  city: string;
}

export interface GeoJSCountryData {
  name?: {
    common: string;
  };
  cca2: string;
}

export interface StepUpCardCheckoutData extends PrepareCardCheckoutData {
  billTo: BillTo;
  securityCode: string;
  feeAmount: number;
  flowData: FlowData;
}

export interface StepUpCardCheckoutResponse {
  flowData: FlowData;
  message: string;
  requestId: string;
  status: string;
  transactionId: string;
  error: string;
}

export const stepUpCardCheckout = async (
  data: StepUpCardCheckoutData
): Promise<StepUpCardCheckoutResponse> => {
  try {
    const response = await api.post<StepUpCardCheckoutResponse>(
      '/v1/checkout/stepup-card',
      data
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<StepUpCardCheckoutResponse>;
      if (axiosError.response?.data?.error) {
        throw new Error(axiosError.response.data.error);
      }
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

export interface MpesaCheckoutData {
  phoneNumber: string;
  description: string;
  secureToken: string;
}

export interface MpesaCheckoutResponse {
  message: string;
  requestId: string;
  status: string;
  transactionId: string;
  error: string;
}

export const mpesaCheckout = async (
  data: MpesaCheckoutData
): Promise<MpesaCheckoutResponse> => {
  try {
    const response = await api.post<MpesaCheckoutResponse>(
      '/v1/checkout/mpesa',
      data
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<MpesaCheckoutResponse>;
      if (axiosError.response?.data?.error) {
        throw new Error(axiosError.response.data.error);
      }
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

export interface CheckoutTxnQueryData {
  CheckoutData: CheckoutData;
  Transaction: Transaction;
}

export interface CheckoutData {
  amount: number;
  curency: string;
  errorCallbackUrl: string;
  successCallbackUrl: string;
}

export interface Transaction {
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  request_id: string;
  transaction_id: string;
  message: string;
}

export interface TransactionResponse {
  transactionId: string;
  status: string;
  message: string;
  amount: number;
  requestId: string;
  currency: string;
  transactionDate: string;
}

export const transactionPolling = async (
  id: string
): Promise<TransactionResponse> => {
  try {
    const response = await api.get<TransactionResponse>('/v1/checkout/status', {
      params: {
        transactionId: id,
      },
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<MpesaCheckoutResponse>;
      if (axiosError.response?.data?.error) {
        throw new Error(axiosError.response.data.error);
      }
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};
